/**

 * Treydora Support tickets (TreydoraEdBackend).

 * Env: REACT_APP_API_BASE_URL

 */

import { getApiBaseUrl } from "../utils/getApiBaseUrl";
import { authHeaders, getAuthToken } from "./authApi";

const API_BASE_URL = getApiBaseUrl();



const SUPPORT_PREFIX = `${API_BASE_URL.replace(/\/$/, "")}/api/support`;



async function parseJson(response) {

  try {

    return await response.json();

  } catch {

    return {};

  }

}



function unwrap(body) {

  if (body && typeof body === "object" && body.success === true && body.data !== undefined) {

    return body.data;

  }

  return body;

}



function readErrorMessage(body, fallback) {

  return body?.message || body?.error || fallback;

}



/** GET /api/support/my-ticket-counts */

export async function fetchMySupportTicketCounts() {

  const token = getAuthToken();

  if (!token) throw new Error("Not authenticated.");

  const res = await fetch(`${SUPPORT_PREFIX}/my-ticket-counts`, {

    headers: { ...authHeaders() },

  });

  const body = await parseJson(res);

  if (!res.ok) {

    throw new Error(readErrorMessage(body, `Unable to load ticket counts (HTTP ${res.status}).`));

  }

  return unwrap(body);

}



/** GET /api/support/my-tickets */

export async function fetchMySupportTickets() {

  const token = getAuthToken();

  if (!token) throw new Error("Not authenticated.");

  const res = await fetch(`${SUPPORT_PREFIX}/my-tickets`, {

    headers: { ...authHeaders() },

  });

  const body = await parseJson(res);

  if (!res.ok) {

    throw new Error(readErrorMessage(body, `Unable to load tickets (HTTP ${res.status}).`));

  }

  const data = unwrap(body);

  return Array.isArray(data) ? data : [];

}



/** GET /api/support/my-tickets/:ticketId */

export async function fetchMySupportTicket(ticketId) {

  const token = getAuthToken();

  if (!token) throw new Error("Not authenticated.");

  const id = encodeURIComponent(String(ticketId || "").trim());

  if (!id) throw new Error("Missing ticket id.");

  const res = await fetch(`${SUPPORT_PREFIX}/my-tickets/${id}`, {

    headers: { ...authHeaders() },

  });

  const body = await parseJson(res);

  if (!res.ok) {

    throw new Error(readErrorMessage(body, `Unable to load ticket (HTTP ${res.status}).`));

  }

  return unwrap(body);

}



/**

 * POST /api/support/tickets

 * @param {object} payload category, priority, subject, description, relatedReference?, relatedWallet?

 * @param {File[]} [files] max 3 — sends multipart when non-empty

 */

export async function createSupportTicket(payload, files = []) {

  const token = getAuthToken();

  if (!token) throw new Error("Not authenticated.");

  const list = Array.isArray(files) ? files.filter(Boolean).slice(0, 3) : [];



  if (list.length === 0) {

    const res = await fetch(`${SUPPORT_PREFIX}/tickets`, {

      method: "POST",

      headers: {

        "Content-Type": "application/json",

        ...authHeaders(),

      },

      body: JSON.stringify(payload),

    });

    const body = await parseJson(res);

    if (!res.ok) {

      throw new Error(readErrorMessage(body, `Could not create ticket (HTTP ${res.status}).`));

    }

    return unwrap(body);

  }



  const fd = new FormData();

  fd.append("category", String(payload.category ?? ""));

  fd.append("priority", String(payload.priority ?? "Normal"));

  fd.append("subject", String(payload.subject ?? ""));

  fd.append("description", String(payload.description ?? ""));

  if (payload.relatedReference) fd.append("relatedReference", String(payload.relatedReference));

  if (payload.relatedWallet) fd.append("relatedWallet", String(payload.relatedWallet));

  list.forEach((f) => fd.append("attachments", f));



  const res = await fetch(`${SUPPORT_PREFIX}/tickets`, {

    method: "POST",

    headers: { ...authHeaders() },

    body: fd,

  });

  const body = await parseJson(res);

  if (!res.ok) {

    throw new Error(readErrorMessage(body, `Could not create ticket (HTTP ${res.status}).`));

  }

  return unwrap(body);

}



/** POST /api/support/my-tickets/:ticketId/messages */

export async function appendSupportTicketMessage(ticketId, messageBody, files = []) {

  const token = getAuthToken();

  if (!token) throw new Error("Not authenticated.");

  const id = encodeURIComponent(String(ticketId || "").trim());

  const list = Array.isArray(files) ? files.filter(Boolean).slice(0, 3) : [];



  if (list.length === 0) {

    const res = await fetch(`${SUPPORT_PREFIX}/my-tickets/${id}/messages`, {

      method: "POST",

      headers: {

        "Content-Type": "application/json",

        ...authHeaders(),

      },

      body: JSON.stringify({ body: messageBody }),

    });

    const body = await parseJson(res);

    if (!res.ok) {

      throw new Error(readErrorMessage(body, `Could not send message (HTTP ${res.status}).`));

    }

    return unwrap(body);

  }



  const fd = new FormData();

  fd.append("body", messageBody != null ? String(messageBody) : "");

  list.forEach((f) => fd.append("attachments", f));



  const res = await fetch(`${SUPPORT_PREFIX}/my-tickets/${id}/messages`, {

    method: "POST",

    headers: { ...authHeaders() },

    body: fd,

  });

  const body = await parseJson(res);

  if (!res.ok) {

    throw new Error(readErrorMessage(body, `Could not send message (HTTP ${res.status}).`));

  }

  return unwrap(body);

}

/** POST /api/support/ai/chat */
export async function postSupportAiChat({ messages, context } = {}) {
  const token = getAuthToken();
  if (!token) throw new Error("Not authenticated.");
  const res = await fetch(`${SUPPORT_PREFIX}/ai/chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify({
      messages: Array.isArray(messages) ? messages : [],
      context: context && typeof context === "object" ? context : undefined,
    }),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    throw new Error(readErrorMessage(body, `AI assistant unavailable (HTTP ${res.status}).`));
  }
  return unwrap(body);
}

/** GET /api/support/ai/verification-summary — current user read-only signals */
export async function getSupportAiVerificationSummary() {
  const token = getAuthToken();
  if (!token) throw new Error("Not authenticated.");
  const res = await fetch(`${SUPPORT_PREFIX}/ai/verification-summary`, {
    method: "GET",
    headers: { ...authHeaders() },
  });
  const body = await parseJson(res);
  if (!res.ok) {
    throw new Error(readErrorMessage(body, `Verification summary unavailable (HTTP ${res.status}).`));
  }
  return unwrap(body);
}

/** POST /api/support/ai/escalate */
export async function postSupportAiEscalate(payload) {
  const token = getAuthToken();
  if (!token) throw new Error("Not authenticated.");
  const res = await fetch(`${SUPPORT_PREFIX}/ai/escalate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify(payload && typeof payload === "object" ? payload : {}),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    throw new Error(readErrorMessage(body, `Could not create ticket from assistant (HTTP ${res.status}).`));
  }
  return unwrap(body);
}


